import { ConnectLogo } from "./connect-logo"
import { Form } from "@/components/form/Form.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { Input, PasswordInput } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { useRef, useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import useSWR from "swr"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { getIntegrations, Integration, upsertIntegration } from "@/api/integration"
import { ComponentChildren } from "preact"

interface ConnectIntegrationFormProps {
  /** Integration name sent to the backend (e.g. "kehe", "wakefern", "cands") */
  integrationName: string
  /** Path to redirect to after successful connection or "continue anyway" */
  redirectTo: string
  /** Logo image source. Omit when no logo asset exists yet — the name is shown instead. */
  logoSrc?: string
  /** Logo alt text, and the heading shown when there is no logo. */
  logoAlt: string
  /** Description text shown below the logo */
  description: string
  /**
   * Labels for the two credential fields. Not every integration authenticates with a username and
   * a password — Vividly wants a company UUID and an API token — and the label is what tells the
   * user which one to paste where.
   */
  usernameLabel?: string
  passwordLabel?: string
  usernamePlaceholder?: string
  passwordPlaceholder?: string
  /** Optional extra content rendered below the form (e.g. help text, skip button) */
  children?: ComponentChildren
  className?: string
}

export function ConnectIntegrationForm({
  integrationName,
  redirectTo,
  logoSrc,
  logoAlt,
  description,
  usernameLabel = "Username",
  passwordLabel = "Password",
  usernamePlaceholder,
  passwordPlaceholder,
  children,
}: ConnectIntegrationFormProps) {
  const [, setLocation] = useLocation()
  const [loading, setLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)

  const form = useFormState({
    username: "",
    password: "",
  })

  // A brand coming back to save a new password sees the login it already has, so the only field
  // left to type is the password. Esti typed its email into the blank Username box on 10/26 and the
  // save replaced its Wakefern user ID with it.
  const { data: existing_result } = useSWR(["integrations", integrationName], () => getIntegrations({ name: integrationName }))
  const saved = existing_result?.ok ? saved_username(existing_result.value.data, integrationName) : null
  const prefilled = useRef(false)
  if (saved && !prefilled.current) {
    prefilled.current = true
    if (form.username.value === "") form.set_form_data({ username: saved })
  }

  async function onSubmit() {
    setLoading(true)
    setLoginError(null)
    form.set_form_errors([])
    const res = await upsertIntegration(integrationName, form.username.value, { password: form.password.value })
    setLoading(false)
    if (!res.ok) {
      setLoginError(res.error.message)
      return
    }
    setLocation(redirectTo)
  }

  function handleContinueAnyway() {
    setLocation(redirectTo)
  }

  return (
    <CenterColumnContainer>
      <Form class="space-y-6" onSubmit={onSubmit} alerts={form.form_alerts}>
        {logoSrc ? (
          <ConnectLogo src={logoSrc} alt={logoAlt} />
        ) : (
          <h1 class="text-2xl font-semibold text-gray-900 text-center">{logoAlt}</h1>
        )}
        <p class="text-gray-500 text-sm">{description}</p>
        {saved && (
          <p class="text-gray-500 text-sm">
            Already connected as <span class="font-medium text-gray-900">{saved}</span>. Enter the new password to update it.
          </p>
        )}
        <Input
          label={usernameLabel}
          type="text"
          name="username"
          required={true}
          bind={form.username}
          placeholder={usernamePlaceholder}
          noAutofill
        />
        <PasswordInput label={passwordLabel} required={true} bind={form.password} placeholder={passwordPlaceholder} noAutofill />
        {loginError && (
          <div class="rounded-md bg-red-50 border border-red-200 p-4 space-y-3">
            <p class="text-sm text-red-700">{loginError}</p>
            <div class="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleContinueAnyway}
              >
                Continue Anyway
              </Button>
            </div>
          </div>
        )}
        <div class="flex justify-end">
          <Button
            type="submit"
            class="bg-gray-900 text-white rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
            disabled={loading}>
            {loading ? "Connecting..." : "Connect Account"}
          </Button>
        </div>
        {loading && <p class="text-sm text-gray-500 text-center">This may take up to 30 seconds</p>}
        {children}
      </Form>
    </CenterColumnContainer>
  )
}

/**
 * The username a brand's existing connection signs in with, when there is exactly one connection
 * of this kind to update. A blank or several-row answer leaves the field for the user to fill.
 */
export function saved_username(integrations: Pick<Integration, "name" | "username">[], name: string): string | null {
  const rows = integrations.filter((i: Pick<Integration, "name" | "username">) => i.name === name)
  if (rows.length !== 1) return null
  const username = rows[0].username.trim()
  return username === "" ? null : username
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("saved_username", () => {
    it("is the one saved login of that kind", () => {
      const rows = [
        { name: "wakefern", username: "WIJLP" },
        { name: "awg", username: "accounting@estifoods.com" },
      ]
      expect(saved_username(rows, "wakefern")).toBe("WIJLP")
      expect(saved_username(rows, "awg")).toBe("accounting@estifoods.com")
    })

    it("is nothing for a first connection, a blank row, or several rows", () => {
      expect(saved_username([], "wakefern")).toBeNull()
      expect(saved_username([{ name: "wakefern", username: " " }], "wakefern")).toBeNull()
      expect(
        saved_username(
          [
            { name: "cands", username: "a@brand.com" },
            { name: "cands", username: "b@brand.com" },
          ],
          "cands"
        )
      ).toBeNull()
    })
  })
}
